import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as api from '@/apis/aipass'
import { HotelBasicInfo, HotelReceiptInfoInput } from '@/models/hotel/basic-info'
import { ProvisoFormData } from '@/models/hotel/receipt-proviso'
import { listContainerStyle } from '@/constants/layout'
import { ReceiptDisplayMethod, ReceiptDisplayMethodType } from '@/constants/receipt-display-method'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { InputField } from '@/components/molecules/input-field'
import { Select, Option } from '@/components/atoms/select'
import { CropModal } from '@/components/molecules/settings/crop-modal'
import { readImageFile, ALLOWED_MIME_TYPES } from '@/utils/image-file'
import { Hint } from '@/components/atoms/hint'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'

// 領収書情報と但し書きは別APIだが、1画面1フッターで保存するため同一フォームで扱う
type ReceiptFormValue = HotelReceiptInfoInput & ProvisoFormData

const toReceiptInfoFormValue = (basicInfo: HotelBasicInfo): HotelReceiptInfoInput => ({
  receiptHotelName: basicInfo.receiptHotelName,
  receiptTelephone: basicInfo.receiptTelephone,
  receiptAddress: basicInfo.receiptAddress,
  receiptLogoImageUrl: basicInfo.receiptLogoImageUrl,
  receiptRegistrationNumber: basicInfo.receiptRegistrationNumber,
  receiptTaxOfficeName: basicInfo.receiptTaxOfficeName,
  receiptDisplayMethod: basicInfo.receiptDisplayMethod ?? ReceiptDisplayMethod.PlanSubjectSet,
})

export const SettingAccountReceiptEdit: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false)
  const [cropImageSrc, setCropImageSrc] = useState<string>('')

  const {
    control,
    register,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ReceiptFormValue>({
    mode: 'all',
    defaultValues: {
      receiptHotelName: '',
      receiptTelephone: '',
      receiptAddress: '',
      receiptLogoImageUrl: null,
      receiptRegistrationNumber: '',
      receiptTaxOfficeName: '',
      receiptDisplayMethod: ReceiptDisplayMethod.PlanSubjectSet,
      receiptProvisos: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'receiptProvisos',
    keyName: 'fieldId',
  })

  const receiptLogoImageUrl = watch('receiptLogoImageUrl')

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const dataUrl = await readImageFile(file, t)
    if (!dataUrl) return
    setCropImageSrc(dataUrl)
    setCropModalOpen(true)
  }

  const onCloseCropModal = () => {
    setCropImageSrc('')
    setCropModalOpen(false)
  }

  const onSaveData = async ({ receiptProvisos, ...receiptInfo }: ReceiptFormValue) => {
    try {
      setIsLoading(true)
      // 税務署名は最大5文字のため、前後の空白で桁を消費しないよう詰めて保存する
      await api.updateFacilityReceiptInfo({ ...receiptInfo, receiptTaxOfficeName: receiptInfo.receiptTaxOfficeName?.trim() ?? null })
      await api.updateReceiptProviso({ receiptProvisos })
      history.push({ pathname: '/setting/account' })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/account',
    onAllowed: () => {
      setIsLoading(true)
      // 2APIの結果を1回の reset にまとめ、後勝ちで一方の値が消えないようにする
      Promise.all([api.fetchFacilityBasicInfo(), api.fetchReceiptProviso()])
        .then(([facility, proviso]) => {
          // fetchFacilityBasicInfo は API 層で例外を握り潰すため undefined を取得失敗とみなす。
          // 未取得のまま編集させると保存時に既存データを空で上書きしてしまう
          if (!facility) {
            alert(t('Communication failed'))
            history.replace({ pathname: '/setting/account' })
            return
          }
          reset({
            ...toReceiptInfoFormValue(facility.basicInfo),
            receiptProvisos: proviso?.receiptProviso ?? [],
          })
        })
        .catch(error => {
          errorHandler(error)
          history.replace({ pathname: '/setting/account' })
        })
        .finally(() => setIsLoading(false))
    },
  })

  const footerContent = <EditFooter onCancel={history.goBack} onSave={handleSubmit(onSaveData)} isDisabledSave={!isValid} />
  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <div css={hotelAccountDetailsContainerStyle}>
        <div css={detailsHeaderContainerStyle}>
          <div css={detailsHeaderTitleStyle}>{t('Receipt')}</div>
        </div>
        <div css={css({ display: 'flex' })}>
          <div css={leftContainerStyle}>
            {cropModalOpen && (
              <CropModal
                value={cropImageSrc}
                title={t('Set receipt logo')}
                aspect={8 / 3}
                onUpload={imageUrl => setValue('receiptLogoImageUrl', imageUrl, { shouldDirty: true })}
                onClose={onCloseCropModal}
                feature="hotel"
                isPublic={true}
              />
            )}
            <ul css={detailsMainContainerStyle}>
              <li css={facilityNameList}>
                <div css={listDetailTitle}>{t('A name of a hotel')}</div>
                <Controller
                  name="receiptHotelName"
                  control={control}
                  rules={{ required: t('Required field has not been entered') }}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      placeholder={'HOTEL CUICIN'}
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li css={facilityNameList}>
                <div css={listDetailTitle}>{t('Phone number')}</div>
                <Controller
                  name="receiptTelephone"
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      placeholder={'08012345678'}
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li css={facilityNameList}>
                <div css={listDetailTitle}>{t('Address')}</div>
                <Controller
                  name="receiptAddress"
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      placeholder={t('1-1 Chiyoda Chiyoda-ku')}
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li css={facilityNameList}>
                <div css={listDetailTitle}>{t('Registration number')}</div>
                <Controller
                  name="receiptRegistrationNumber"
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      maxLength={14}
                      placeholder="T1234567891234"
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li css={facilityNameList}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div css={listDetailTitle}>{t('Tax office name')}</div>
                  <Hint customCss={taxOfficeHintStyle}>
                    <div className="inner-block">
                      <p>{t('tax office name hint')}</p>
                    </div>
                  </Hint>
                </div>
                <Controller
                  name="receiptTaxOfficeName"
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <InputField
                      marginBottom={0}
                      width="90%"
                      maxLength={5}
                      placeholder={t('Shibuya')}
                      value={value ?? ''}
                      name={name}
                      handleChangeData={onChange}
                      error={error?.message}
                    />
                  )}
                />
              </li>
              <li css={facilityNameList}>
                <div css={listDetailTitle}>{t('Receipt display method')}</div>
                <Controller
                  name="receiptDisplayMethod"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      value={value ?? ReceiptDisplayMethod.PlanSubjectSet}
                      onChange={v => onChange(v as ReceiptDisplayMethodType)}
                      customStyle={css({ width: '90%', paddingBottom: 0 })}
                    >
                      <Option value={ReceiptDisplayMethod.PlanSubjectSet}>{t('Plan name and subject set name')}</Option>
                      <Option value={ReceiptDisplayMethod.PlanSubjectSetByDate}>{t('Plan name and subject set name by date')}</Option>
                      <Option value={ReceiptDisplayMethod.SubjectName}>{t('Subject name')}</Option>
                    </Select>
                  )}
                />
              </li>
              <li css={facilityNameList}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div css={listDetailTitle}>{t('Receipt logo')}</div>
                  <Hint customCss={receiptLogoHintStyle}>
                    <div className="inner-block">
                      <p>{t('The only supported image formats are JPG and PNG.')}</p>
                      <p>{t('Recommended size: 400px × 150px')}</p>
                    </div>
                  </Hint>
                </div>
                <div css={receiptLogoImageWrapperStyle}>
                  <label css={receiptLogoImageInputStyle} htmlFor="receiptLogoImage" />
                  <input
                    type="file"
                    id="receiptLogoImage"
                    css={noneStyle}
                    onChange={handleChangeFile}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                      e.currentTarget.value = ''
                    }}
                    accept={ALLOWED_MIME_TYPES.join(',')}
                  />
                  {receiptLogoImageUrl && (
                    <div css={receiptLogoImagePreviewStyle} style={{ backgroundImage: `url(${receiptLogoImageUrl})` }}>
                      <img
                        src={require('@/static/images/deleat_icon.svg')}
                        css={deleteIconStyle}
                        alt="delete icon"
                        onClick={e => {
                          e.stopPropagation()
                          setValue('receiptLogoImageUrl', null, { shouldDirty: true })
                        }}
                      />
                    </div>
                  )}
                </div>
              </li>
            </ul>
          </div>
          <div css={rightContainerStyle}>
            <ul css={detailsMainContainerStyle}>
              <li css={facilityNameList}>
                <div css={listDetailTitle}>{t('Proviso')}</div>
                {fields.map((field, index) => {
                  const error = errors.receiptProvisos?.[index]?.proviso
                  return (
                    <div key={field.fieldId} css={provisoItemStyle}>
                      <input type="hidden" {...register(`receiptProvisos.${index}.id`)} />
                      <input type="hidden" {...register(`receiptProvisos.${index}.isDefault`)} />
                      <div css={provisoInputWrapperStyle}>
                        <input
                          type="text"
                          {...register(`receiptProvisos.${index}.proviso`, {
                            required: t('Required field has not been entered'),
                          })}
                          css={[provisoInputStyle, error && provisoInputErrorStyle]}
                          placeholder={t('Add proviso')}
                        />
                        {error && <span css={provisoErrorMessageStyle}>{error.message}</span>}
                      </div>
                      {/* 但し書きがデフォルトのものは削除禁止 */}
                      {!field.isDefault && <DeleteIcon onClick={() => remove(index)} css={provisoDeleteIconStyle} />}
                    </div>
                  )
                })}
                <AddFormFieldButton
                  label={t('Add proviso')}
                  onAdd={() => {
                    append({ proviso: '', isDefault: false })
                  }}
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </SettingsLayout>
  )
}
// topStyle
const hotelAccountDetailsContainerStyle = css(listContainerStyle, {
  width: '100%',
  background: '#FFF',
  minHeight: 410,
})

const leftContainerStyle = css({
  paddingTop: 20,
  display: 'flex',
  width: '50%',
  minHeight: 360,
  borderBottom: '1px solid #F2F2F2',
})

const rightContainerStyle = css({
  padding: '20px 40px 0 0',
  width: '50%',
  borderLeft: '1px solid #F2F2F2',
})

const detailsHeaderContainerStyle = css({
  height: 50,
  padding: '0 32px',
  borderBottom: '1px solid #F2F2F2',
})

const detailsHeaderTitleStyle = css({
  fontSize: 18,
  fontWeight: 'bold',
  padding: '16px 0',
})

const detailsMainContainerStyle = css({
  width: '100%',
  marginBottom: 36,
})

const hotelLogoList = css({
  margin: '16px 0px 0 35px',
})
const facilityNameList = css(hotelLogoList)
const listDetailTitle = css({
  fontSize: '12px',
  fontWeight: 'bold',
  color: '#676767',
  margin: '10px 0 10px 0',
})

const noneStyle = css({
  display: 'none',
})

const receiptLogoImageWrapperStyle = css({
  width: 400,
})

const receiptLogoImageInputStyle = css({
  cursor: 'pointer',
  display: 'block',
  width: '100%',
  height: 150,
  '&::after': {
    display: 'inline-block',
    width: '100%',
    height: 150,
    content: '""',
    verticalAlign: 'middle',
    backgroundImage: `url(${require('@/static/images/camera_icon_white.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    backgroundSize: 30,
    borderRadius: 5,
  },
})

const receiptLogoImagePreviewStyle = css({
  width: '100%',
  height: 150,
  margin: '-150px 0 0 0',
  borderRadius: 5,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  pointerEvents: 'none',
  position: 'relative',
})

const deleteIconStyle = css({
  position: 'absolute',
  top: -9,
  right: -9,
  width: 20,
  height: 20,
  cursor: 'pointer',
  pointerEvents: 'auto',
})

const receiptLogoHintStyle = css({
  marginLeft: 8,
  '.help-popup': { left: 20, top: 0 },
  '.inner-block': {
    width: 310,
    padding: 14,
    textAlign: 'center',
    p: {
      color: '#676767',
      fontWeight: 'bold',
      textAlign: 'left',
      lineHeight: 1.5,
      letterSpacing: 1.2,
      fontSize: 12,
    },
  },
})

// Proviso item styles
const provisoItemStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  marginBottom: '12px',
})

const provisoInputWrapperStyle = css({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
})

const provisoInputStyle = css({
  padding: '9px 16px',
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  fontSize: 14,
  lineHeight: '14px',
  height: 32,
  fontFamily: 'inherit',
  '::placeholder': {
    color: '#CCCCCC',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#F2A40B',
  },
})

const provisoInputErrorStyle = css({
  borderColor: '#ff2700 !important',
})

const provisoErrorMessageStyle = css({
  fontSize: 10,
  color: 'rgba(255,0,0,0.5)',
  marginTop: 4,
})

const provisoDeleteIconStyle = css({
  flexShrink: 0,
  cursor: 'pointer',
  marginTop: 4,
})

const taxOfficeHintStyle = css({
  marginLeft: 8,
  '.help-popup': { left: 20, top: 0 },
  '.inner-block': {
    width: 350,
    padding: 14,
    textAlign: 'center',
    p: {
      color: '#676767',
      fontWeight: 'bold',
      textAlign: 'left',
      lineHeight: 1.5,
      letterSpacing: 1.2,
      fontSize: 12,
    },
  },
})
